import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "react-tabs/style/react-tabs.css";
import toast, { Toaster } from "react-hot-toast";
import { Table } from "react-bootstrap";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
    getStoreTransferDetails,
    getStoreTransferHistory,
    storeTransferApprove,
    storeTransferCancel,
} from "../../../services/Inventory/StoreTransferService";

const loc = (store, rack, cell, pallet) => [store, rack, cell, pallet].map((v) => v || "N/A").join(" / ");

const StoreTransferView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [record, setRecord] = useState(null);
    const [history, setHistory] = useState([]);
    const [tabIndex, setTabIndex] = useState(0);
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        getStoreTransferDetails(id).then((res) => {
            if (res.errorCode === 0) {
                setRecord(res.responsePacket);
            } else {
                toast.error(res.message);
            }
        }).catch(() => toast.error("Could not load store transfer."));
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    const loadHistory = () => {
        if (!record?.storeTransferCode) return;
        getStoreTransferHistory(record.storeTransferCode).then((res) => {
            if (res.errorCode === 0) {
                setHistory(res.responsePacket || []);
            } else {
                toast.error(res.message);
            }
        });
    };

    const handleApprove = () => {
        if (!window.confirm(`Approve ${record.storeTransferCode}? Stock will move to the new location.`)) return;
        setBusy(true);
        storeTransferApprove(record.uuid).then((res) => {
            if (res.errorCode === 0) {
                toast.success(res.message);
                load();
            } else {
                toast.error(res.message);
            }
        }).catch(() => toast.error("Approve failed. Please try again."))
            .finally(() => setBusy(false));
    };

    const handleCancel = () => {
        if (!window.confirm(`Cancel ${record.storeTransferCode}?`)) return;
        setBusy(true);
        storeTransferCancel(record.uuid).then((res) => {
            if (res.errorCode === 0) {
                toast.success(res.message);
                load();
            } else {
                toast.error(res.message);
            }
        }).catch(() => toast.error("Cancel failed. Please try again."))
            .finally(() => setBusy(false));
    };

    const statusColor = { Pending: "#f0ad4e", Approved: "#28a745", Cancelled: "#dc3545" };

    return (
        <>
            <div className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3 className="mb-0 font-size-18 font-weight-bolder text-uppercase">
                        <ArrowBackIcon style={{ cursor: "pointer" }} onClick={() => navigate("/storeTransfer/list")} />
                        &nbsp;Store Transfer {record?.storeTransferCode || ""}
                    </h3>
                    {record?.storeTransferStatusEnum === "Pending" && (
                        <div>
                            <button className="btn btn-success mr-2" disabled={busy} onClick={handleApprove}>
                                <ThumbUpAltIcon fontSize="small" /> Approve
                            </button>
                            <button className="btn btn-danger" disabled={busy} onClick={handleCancel}>
                                <HighlightOffIcon fontSize="small" /> Cancel
                            </button>
                        </div>
                    )}
                </div>

                {record && (
                    <Tabs selectedIndex={tabIndex} onSelect={(index) => setTabIndex(index)} className="upperTabs">
                        <TabList>
                            <Tab>Details</Tab>
                            <Tab onClick={loadHistory}>Log History</Tab>
                        </TabList>

                        <TabPanel>
                            <div className="row mb-3">
                                <div className="col-md-3"><b>Warehouse:</b> {record.warehouseTitle}</div>
                                <div className="col-md-3"><b>Date:</b> {record.storeTransferDate}</div>
                                <div className="col-md-3">
                                    <b>Status:</b>{" "}
                                    <span style={{ color: statusColor[record.storeTransferStatusEnum] || "inherit", fontWeight: 600 }}>
                                        {record.storeTransferStatusEnum}
                                    </span>
                                </div>
                                <div className="col-md-3"><b>Created By:</b> {record.storeTransferByUserTitle}</div>
                            </div>
                            <div className="row mb-3">
                                {record.approvedUserTitle && (
                                    <div className="col-md-3">
                                        <b>{record.storeTransferStatusEnum === "Cancelled" ? "Cancelled By:" : "Approved By:"}</b> {record.approvedUserTitle}
                                    </div>
                                )}
                                {record.note && <div className="col-md-9"><b>Note:</b> {record.note}</div>}
                            </div>

                            <div style={{ overflowX: "auto" }}>
                                <Table bordered hover size="sm">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Item</th>
                                            <th>Batch No</th>
                                            <th>Mfg</th>
                                            <th>Expiry</th>
                                            <th className="text-right">Qty</th>
                                            <th>From (Store / Rack / Cell / Pallet)</th>
                                            <th>To (Store / Rack / Cell / Pallet)</th>
                                            <th>Reference</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {record.items?.map((it, i) => (
                                            <tr key={it.uuid}>
                                                <td>{i + 1}</td>
                                                <td>{it.itemTitle}</td>
                                                <td>{it.batchNumber}</td>
                                                <td>{it.batchManufacturingDate}</td>
                                                <td>{it.batchExpiredDate}</td>
                                                <td className="text-right">{it.quantity}</td>
                                                <td>{loc(it.fromStoreTitle, it.fromRackTitle, it.fromCellTitle, it.fromPalletTitle)}</td>
                                                <td>{loc(it.toStoreTitle, it.toRackTitle, it.toCellTitle, it.toPalletTitle)}</td>
                                                <td>{it.sourceReferenceNo}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </TabPanel>

                        <TabPanel>
                            <Table bordered size="sm">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Time</th>
                                        <th>Action</th>
                                        <th>User</th>
                                        <th>Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.length === 0 && (
                                        <tr><td colSpan={5} className="text-center">No history</td></tr>
                                    )}
                                    {history.map((h, i) => (
                                        <tr key={i}>
                                            <td>{h.transactionDate?.substring(0, 10)}</td>
                                            <td>{h.transactionDate?.split("T")[1]?.substring(0, 5)}</td>
                                            <td>{h.actionOnItemEnum}</td>
                                            <td>{h.userName}</td>
                                            <td>
                                                <div dangerouslySetInnerHTML={{
                                                    __html: (h.message || "").replaceAll("\n", "<br />").replaceAll("\t", "&nbsp;&nbsp;&nbsp;&nbsp;"),
                                                }} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </TabPanel>
                    </Tabs>
                )}
            </div>
            <Toaster />
        </>
    );
};

export default StoreTransferView;
